/* eslint-disable react/no-array-index-key */
import React, { useEffect, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { gapPxToDegrees, buildRoundedSectorPath } from "../../utils/geometry";
import "./styles.sass";

type WheelSkeletonProps = {
  segments?: number;
  cornerRadius?: number;
  gapWidth?: number;
  fallbackDiameter?: number;
};

const WheelSkeleton: React.FC<WheelSkeletonProps> = ({
  segments = 8,
  cornerRadius = 14,
  gapWidth = 6,
  fallbackDiameter = 560,
}) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [size, setSize] = useState(fallbackDiameter);

  useEffect(() => {
    const el = svgRef.current;
    if (!el) {
      return;
    }

    const measure = () => {
      const rect = el.getBoundingClientRect();
      const s = Math.max(0, Math.min(rect.width, rect.height || rect.width));
      if (s > 0) {
        setSize(s);
      }
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const outerRadius = size / 2;
  const sectorAngle = 360 / Math.max(1, segments);
  const gapAngle = gapPxToDegrees(outerRadius, gapWidth);
  const wedgeAngle = sectorAngle - gapAngle;
  const halfWedge = wedgeAngle / 2;

  const viewBox = `${-outerRadius} ${-outerRadius} ${size} ${size}`;

  return (
    <div className="WheelSkeleton" aria-hidden>
      <div className="WheelSkeleton__stage">
        <svg ref={svgRef} className="WheelSkeleton__svg" viewBox={viewBox}>
          <defs>
            {Array.from({ length: segments }).map((_, i) => {
              const startAngle = -halfWedge + i * sectorAngle;
              const endAngle = halfWedge + i * sectorAngle;
              const d = buildRoundedSectorPath(
                outerRadius,
                startAngle,
                endAngle,
                cornerRadius,
              );
              return (
                <clipPath key={i} id={`ws-clip-${i}`}>
                  <path d={d} />
                </clipPath>
              );
            })}
          </defs>

          {Array.from({ length: segments }).map((_, i) => (
            <g key={i} clipPath={`url(#ws-clip-${i})`}>
              <foreignObject
                x={-outerRadius}
                y={-outerRadius}
                width={size}
                height={size}
                className="WheelSkeleton__fo"
              >
                <div className="WheelSkeleton__fill">
                  <Skeleton width="100%" height="100%" />
                </div>
              </foreignObject>
            </g>
          ))}
        </svg>
      </div>
      <div className="WheelSkeleton-action">
        <div className="WheelSkeleton-action__bg">
          <Skeleton width={300} height={96} borderRadius={20} />
        </div>

        <Skeleton
          className="WheelSkeleton-action__line"
          width={165}
          height={16}
        />

        <Skeleton
          className="WheelSkeleton-action__button"
          width={276}
          height={56}
        />
      </div>
    </div>
  );
};

export default WheelSkeleton;
